import type { ComputedRef, Ref } from "vue";
import { computed, ref, watch } from "vue";

import { getStatusDistribution } from "@/backend/read/BackendReader";
import type { StatusClass } from "@/model/baseTypes";
import type { Iteration } from "@/model/session";
import type { StatusDistributionSource } from "@/model/status";

export interface StatusDistributionRow {
  statusName: string;
  statusClass: StatusClass;
  totalStoryPoints: number;
  itemsCount: number;
  stickyIds: string[];
}

interface StatusDistributionDetailedData {
  rows: StatusDistributionRow[];
  source: StatusDistributionSource;
}

export interface StatusDistributionFilters {
  boardId?: string;
  teamId?: string;
  iterationId?: number;
  artId?: number;
  stickyId?: string;
  objectiveId?: string;
}

interface UseStatusDistributionOptions {
  filters: ComputedRef<StatusDistributionFilters>;
  source: StatusDistributionSource;
  iteration?: Iteration;
}

export function useStatusDistribution(options: UseStatusDistributionOptions): {
  data: ComputedRef<StatusDistributionDetailedData>;
  loading: Ref<boolean>;
  error: Ref<boolean>;
  fetch: () => Promise<void>;
} {
  const loading = ref(false);
  const error = ref(false);
  const rows = ref<StatusDistributionRow[]>([]);

  const data = computed<StatusDistributionDetailedData>(() => ({
    iteration: options.iteration,
    rows: rows.value,
    source: options.source,
  }));

  const fetch = async () => {
    loading.value = true;
    error.value = false;

    try {
      const response = await getStatusDistribution(options.filters.value);
      rows.value = response as StatusDistributionRow[];
    } catch (err) {
      error.value = true;
      rows.value = [];
      throw err;
    } finally {
      loading.value = false;
    }
  };

  watch(
    () => options.filters.value,
    (newFilters, oldFilters) => {
      const filtersChanged = JSON.stringify(newFilters) !== JSON.stringify(oldFilters);
      if (filtersChanged && (rows.value.length > 0 || loading.value || error.value)) {
        void fetch();
      }
    },
    { deep: true },
  );

  return { data, loading, error, fetch };
}
